/**
 * Claims API client (Tasks #20024/#20025 — DSv2 S4.1/S4.2 web).
 *
 * Thin authenticated wrappers over the dark-gated claims endpoints,
 * following the same bearer-token `authFetch` convention as
 * `users-api.ts` / `safety-api.ts` (transient GET retry, RateLimitError on
 * 429, one silent refresh on 401). While the claims gate is off
 * the server answers 404 on everything here — callers treat that as
 * "surface does not exist".
 */

import { fetchWithTransientRetry } from "@/lib/transient-fetch";
import { RateLimitError } from "@/lib/api-error";
import { getSharedRefreshedToken, forceSessionExpiredRedirect } from "@/lib/auth-refresh";
import { ApiError } from "@/lib/users-api";

const API_BASE = `${import.meta.env.BASE_URL || "/"}api`;

function authHeaders(token: string): HeadersInit {
  return {
    "Content-Type": "application/json",
    Authorization: `Bearer ${token}`,
  };
}

async function authFetch<T>(path: string, token: string, init?: RequestInit): Promise<T> {
  const res = await fetchWithTransientRetry(`${API_BASE}${path}`, {
    ...init,
    headers: { ...authHeaders(token), ...init?.headers },
  });
  if (res.status === 204) return undefined as T;
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    if (res.status === 429) {
      const retryAfterHeader = Number(res.headers.get("Retry-After"));
      const retryAfter =
        Number.isFinite(retryAfterHeader) && retryAfterHeader > 0 ? retryAfterHeader : 60;
      throw new RateLimitError(
        (typeof body?.message === "string" && body.message) ||
          "Too many requests. Please try again shortly.",
        retryAfter,
      );
    }
    if (res.status === 401) {
      const freshToken = await getSharedRefreshedToken();
      if (freshToken && freshToken !== token) {
        const retryRes = await fetchWithTransientRetry(`${API_BASE}${path}`, {
          ...init,
          headers: { ...authHeaders(freshToken), ...init?.headers },
        });
        if (retryRes.status === 204) return undefined as T;
        if (retryRes.ok) return retryRes.json();
        const retryBody = await retryRes.json().catch(() => ({}));
        if (retryRes.status === 401 || retryBody.error === "session_expired") {
          forceSessionExpiredRedirect();
        }
        throw new ApiError(
          (typeof retryBody?.message === "string" && retryBody.message) ||
            retryBody.error ||
            `API error ${retryRes.status}`,
          retryRes.status,
          retryBody,
        );
      }
    }
    throw new ApiError(
      (typeof body?.message === "string" && body.message) ||
        body.error ||
        `API error ${res.status}`,
      res.status,
      body,
    );
  }
  return res.json();
}

// ---------------------------------------------------------------------------
// Types (mirror artifacts/api-server/src/routes/claims.ts responses).
// ---------------------------------------------------------------------------

export type ClaimStatusValue =
  | "draft"
  | "submitted"
  | "under_review"
  | "awaiting_info"
  | "sent_to_insurer"
  | "approved"
  | "paid"
  | "denied"
  | "closed"
  | "withdrawn"
  | "auto_resolved"
  | "abandoned";

export interface ClaimSummary {
  id: number;
  trip_id: number;
  claim_status: ClaimStatusValue;
  category: string | null;
  description: string | null;
  amount_cents: number | null;
  currency: string;
  reference: string | null;
  submitted_at: string | null;
  created_at: string;
  updated_at: string;
  trips?: {
    public_id: string | null;
    started_at: string | null;
    drop_off: string | null;
    cars?: { year: number | null; make: string | null; model: string | null } | null;
  } | null;
}

export type ClaimEvidenceKind = "photo" | "estimate" | "invoice" | "police_report" | "statement" | "other";

export interface ClaimEvidenceItem {
  id: number;
  kind: ClaimEvidenceKind;
  source: "host" | "renter" | "admin" | "inspection_auto";
  storage_key: string | null;
  file_url: string | null;
  filename: string | null;
  mime_type: string | null;
  note: string | null;
  created_at: string;
}

export interface ClaimTrackerStage {
  key: "filed" | "review" | "insurer" | "outcome";
  label: string;
  state: "done" | "current" | "upcoming";
}

export interface ClaimDetail {
  claim: ClaimSummary & { host_id: string; renter_id: string };
  evidence: ClaimEvidenceItem[];
  tracker: {
    stages: ClaimTrackerStage[];
    terminal: boolean;
    outcome: ClaimStatusValue | null;
    needsInfo: boolean;
  };
  trip: { id: number; public_id: string | null };
  events?: Array<{ id: number; action: string; payload: unknown; created_at: string }>;
}

export const CLAIM_STATUS_LABELS: Record<ClaimStatusValue, string> = {
  draft: "Draft",
  submitted: "Submitted",
  under_review: "In review",
  awaiting_info: "More info needed",
  sent_to_insurer: "With the insurer",
  approved: "Approved",
  paid: "Paid",
  denied: "Not approved",
  closed: "Closed",
  withdrawn: "Withdrawn",
  auto_resolved: "Resolved",
  abandoned: "Abandoned",
};

export const CLAIM_CATEGORY_LABELS: Record<string, string> = {
  damage_exterior: "Exterior damage",
  damage_interior: "Interior damage",
  damage_mechanical: "Mechanical damage",
  missing_item: "Missing item",
  smoking: "Smoking",
  excess_mileage: "Excess mileage",
  fuel: "Fuel not replaced",
  late_return: "Late return",
  cleaning: "Cleaning",
  other: "Something else",
};

// ---------------------------------------------------------------------------
// Endpoints.
// ---------------------------------------------------------------------------

export const claimsApi = {
  listMine(token: string): Promise<{ claims: ClaimSummary[] }> {
    return authFetch(`/claims/mine`, token);
  },

  getClaim(token: string, claimId: number): Promise<ClaimDetail> {
    return authFetch(`/claims/${claimId}`, token);
  },

  updateClaim(
    token: string,
    tripId: number,
    claimId: number,
    fields: { category?: string | null; description?: string | null; amount_cents?: number | null },
  ): Promise<{ claim: ClaimSummary }> {
    return authFetch(`/bookings/${tripId}/claims/${claimId}`, token, {
      method: "PATCH",
      body: JSON.stringify(fields),
    });
  },

  submitClaim(token: string, tripId: number, claimId: number): Promise<{ claim: ClaimSummary }> {
    return authFetch(`/bookings/${tripId}/claims/${claimId}/submit`, token, {
      method: "POST",
      body: JSON.stringify({}),
    });
  },

  /** Signed-PUT upload → bind, mirroring the mobile pipeline. */
  async uploadEvidence(
    token: string,
    tripId: number,
    claimId: number,
    file: File,
    kind: ClaimEvidenceKind,
    note?: string,
  ): Promise<{ evidence: ClaimEvidenceItem }> {
    const base = `/bookings/${tripId}/claims/${claimId}/evidence`;
    const contentType = file.type || "image/jpeg";
    const minted = await authFetch<{ uploadURL: string; objectPath: string; uploadToken: string }>(
      `${base}/request-upload-url`,
      token,
      { method: "POST", body: JSON.stringify({ contentType }) },
    );
    const putRes = await fetch(minted.uploadURL, {
      method: "PUT",
      body: file,
      headers: { "Content-Type": contentType },
    });
    if (!putRes.ok) throw new Error(`Storage upload failed (${putRes.status})`);
    return authFetch(`${base}`, token, {
      method: "POST",
      body: JSON.stringify({
        objectPath: minted.objectPath,
        uploadToken: minted.uploadToken,
        kind,
        mimeType: contentType,
        filename: file.name?.slice(0, 200),
        note,
      }),
    });
  },

  getThread(
    token: string,
    tripId: number,
    claimId: number,
  ): Promise<{ thread: { public_id: string } | null; supportConfigured: boolean }> {
    return authFetch(`/bookings/${tripId}/claims/${claimId}/thread`, token);
  },

  openThread(token: string, tripId: number, claimId: number): Promise<{ thread: { public_id: string } }> {
    return authFetch(`/bookings/${tripId}/claims/${claimId}/thread`, token, {
      method: "POST",
      body: JSON.stringify({}),
    });
  },

  /**
   * Authenticated evidence file fetch → object URL for <img>. The proxy
   * endpoints require the bearer header, which an <img src> can't carry.
   * Caller revokes the URL on unmount.
   */
  async fetchEvidenceObjectUrl(token: string, fileUrl: string): Promise<string> {
    const path = fileUrl.startsWith("/api/") ? fileUrl.slice(4) : fileUrl;
    const res = await fetch(`${API_BASE}${path}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!res.ok) throw new Error(`Evidence fetch failed (${res.status})`);
    return URL.createObjectURL(await res.blob());
  },
};

export function claimCarTitle(claim: ClaimSummary): string {
  const car = claim.trips?.cars;
  if (!car) return "";
  return [car.year, car.make, car.model].filter(Boolean).join(" ");
}

export function claimAmountLabel(claim: Pick<ClaimSummary, "amount_cents">): string | null {
  if (claim.amount_cents == null) return null;
  return `$${(claim.amount_cents / 100).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

/**
 * Base classes shared with the booking status pill (see
 * OwnerBookingDetailPage's STATUS_TONE span) so claims speak the exact same
 * pill language as every dashboard surface.
 */
export const CLAIM_PILL_BASE =
  "inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-semibold uppercase tracking-wide ring-1";

/** Tone classes per status — the house shade families (amber = attention,
 * blue = in motion, emerald = positive, red = negative, gray = terminal). */
export function claimStatusBadgeClass(status: ClaimStatusValue): string {
  switch (status) {
    case "draft":
    case "submitted":
    case "awaiting_info":
      return "bg-amber-50 text-amber-800 ring-amber-200";
    case "under_review":
    case "sent_to_insurer":
      return "bg-blue-50 text-blue-800 ring-blue-200";
    case "approved":
    case "paid":
      return "bg-emerald-50 text-emerald-800 ring-emerald-200";
    case "denied":
      return "bg-red-50 text-red-700 ring-red-200";
    default:
      return "bg-gray-100 text-gray-700 ring-gray-200";
  }
}
